/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaWhatsapp } from 'react-icons/fa';

import { AUTHOR_NAME, COPYRIGHT_DEFAULT_YEAR, SITE_DOMAIN } from '../../constants';
import { ButtonLinkType } from '../../graphql-fragments/ButtonLink';
import { SanityImageType } from '../../graphql-fragments/SanityImage';
import { BackgroundColorTheme } from '../../templates/Page';
import { isEntryActive } from '../../utils/projectUtils';
import { getUrlFromVersatileLink } from '../../utils/sanity';
import { clsx } from '../../utils/utils';
import ButtonLink from './ButtonLink';
import * as styles from './Footer.module.scss';
import Form from './Form';
import SmartLink from './SmartLink';
import TextLink from './TextLink';

export type NavigationBlock = {
  title?: string;
  links: Array<ButtonLinkType>;
};

type SocialNetworkIconName = 'facebook' | 'linkedin' | 'whatsapp' | 'instagram';
export type SocialNetworksBlock = {
  title?: string;
  socialNetworks?: Array<{
    iconName: SocialNetworkIconName;
    url?: string;
  }>;
};

type CtaBlock = {
  title?: string;
  ctaButton?: ButtonLinkType;
};

export type NewsletterBlock = {
  title: string;
  buttonText: string;
  onNewsletterSubscribe?: (event: React.MouseEvent) => void;
};

export interface FooterProps {
  logoImage: SanityImageType;
  theme?: BackgroundColorTheme;
  companyDescription?: string;
  socialNetworksBlock?: SocialNetworksBlock;
  navigationBlock?: NavigationBlock;
  ctaBlock?: CtaBlock;
  newsletterBlock?: NewsletterBlock;
  policyLinks?: Array<ButtonLinkType>;
  experiencesPageSlug: string;
  experiencesPageTextButton: string;
}

const Footer = ({
  logoImage,
  theme,
  companyDescription,
  navigationBlock,
  ctaBlock,
  newsletterBlock,
  policyLinks,
  socialNetworksBlock,
  experiencesPageSlug,
  experiencesPageTextButton,
}: FooterProps): React.ReactElement => {
  const { t } = useTranslation();
  const [currentYear, setCurrentYear] = useState<number>(COPYRIGHT_DEFAULT_YEAR);
  const [hasDocumentLoaded, setHasDocumentLoaded] = useState(false);

  useEffect(() => {
    setCurrentYear(new Date().getFullYear());
    setHasDocumentLoaded(true);
  }, []);

  function renderLogo(logoImage: SanityImageType) {
    return (
      <SmartLink to="/" className={styles.logoContainer}>
        <img src={logoImage.asset.url} className={styles.logo} height={60} alt="logo" />
      </SmartLink>
    );
  }

  function renderCopyright() {
    return (
      <div className={styles.copyright}>
        <span>
          © {currentYear}{' '}
          {t('footer.copyright_right_side_text', '{{copyrightHolderName}}. All Rights Reserved. ', {
            copyrightHolderName: AUTHOR_NAME,
          })}
        </span>
      </div>
    );
  }

  function renderIcon(title: string) {
    const iconsByTitle = {
      facebook: FaFacebookF,
      linkedin: FaLinkedinIn,
      whatsapp: FaWhatsapp,
      instagram: FaInstagram,
    };
    const Element = iconsByTitle[title];
    return (
      <>
        <Element className={styles.icon} />
        <div className={styles.visuallyHidden}>
          Visit {AUTHOR_NAME} {title}
        </div>
      </>
    );
  }

  const withNavigationBlock = !!(
    navigationBlock &&
    navigationBlock.links &&
    navigationBlock.links.length > 0
  );

  const withSocialNetworksBlock = !!(
    socialNetworksBlock &&
    socialNetworksBlock.socialNetworks &&
    socialNetworksBlock.socialNetworks?.length > 0
  );

  const withCtaBlock = !!(ctaBlock && ctaBlock.title && ctaBlock.ctaButton);

  const isFooterComplex = !!(
    companyDescription ||
    withNavigationBlock ||
    withCtaBlock ||
    newsletterBlock ||
    (policyLinks && policyLinks.length > 0) ||
    withSocialNetworksBlock
  );

  return (
    <footer className={styles.footer} data-theme={theme}>
      {isFooterComplex ? (
        <div className={styles.footerContainer}>
          <div className={styles.footerWrapper}>
            <div className={styles.infoContainer}>
              {renderLogo(logoImage)}
              {companyDescription && (
                <div className={styles.companyDescription}>{companyDescription}</div>
              )}
              {(withNavigationBlock ||
                withSocialNetworksBlock ||
                withCtaBlock ||
                newsletterBlock) && (
                <div className={styles.linksAndNewsLetterContainer}>
                  {(withNavigationBlock || withSocialNetworksBlock || withCtaBlock) && (
                    <div className={styles.columnsContainer}>
                      {withNavigationBlock && (
                        <div className={styles.columnContainer}>
                          <span className={styles.columnTitle}>
                            {navigationBlock.title ||
                              t('footer.navigation.default_title', 'Navigation')}
                          </span>
                          {navigationBlock.links.map((link, i) => (
                            <TextLink
                              to={
                                link.linkToExperiencesPage
                                  ? experiencesPageSlug
                                  : getUrlFromVersatileLink(link)
                              }
                              key={i}
                              className={clsx(styles.link, isEntryActive(link) && styles.active)}
                            >
                              {link.linkToExperiencesPage ? experiencesPageTextButton : link.text}
                            </TextLink>
                          ))}
                        </div>
                      )}
                      {withSocialNetworksBlock && (
                        <div className={styles.columnContainer}>
                          <span className={styles.columnTitle}>
                            {socialNetworksBlock.title ||
                              t('footer.social_networks.default_title', 'Follow Us')}
                          </span>
                          <div className={styles.socialMediaContainer}>
                            {socialNetworksBlock.socialNetworks?.map(
                              (socialNetwork, i) =>
                                socialNetwork.url && (
                                  <SmartLink key={i} to={socialNetwork.url}>
                                    {renderIcon(socialNetwork.iconName)}
                                  </SmartLink>
                                ),
                            )}
                          </div>
                        </div>
                      )}
                      {withCtaBlock && (
                        <div className={styles.columnContainer}>
                          <span className={styles.columnTitle}>{ctaBlock.title}</span>
                          {ctaBlock.ctaButton && (
                            <ButtonLink
                              button={ctaBlock.ctaButton}
                              className={styles.ctaButton}
                            ></ButtonLink>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {newsletterBlock && hasDocumentLoaded && (
                    <div className={styles.newsletterContainer}>
                      <span className={styles.columnTitle}>{newsletterBlock.title}</span>
                      <Form
                        hideLabels
                        withSubmitButtonInsideInput
                        submitButtonText={newsletterBlock.buttonText}
                        thankYouScreen={{
                          title: t('footer.newsletter_success', 'Thank You for subscribing!'),
                        }}
                        showThankYouScreenAsLabel
                        fields={[
                          {
                            crmFieldId: 'email',
                            id: 'email',
                            fieldType: 'textSingleLine',
                            title: 'Email',
                            withEmailValidation: true,
                          },
                        ]}
                      />
                    </div>
                  )}{' '}
                </div>
              )}
            </div>
          </div>
          <div className={styles.divider}></div>
          <div className={styles.legalContainer}>
            {policyLinks && policyLinks.length > 0 && (
              <div className={styles.policyLinksContainer}>
                {policyLinks.map((link, i) => (
                  <TextLink
                    to={
                      link.linkToExperiencesPage
                        ? experiencesPageSlug
                        : getUrlFromVersatileLink(link)
                    }
                    key={i}
                    className={clsx(styles.link, isEntryActive(link) && styles.active)}
                  >
                    {link.linkToExperiencesPage ? experiencesPageTextButton : link.text}
                  </TextLink>
                ))}
              </div>
            )}
            <div className={styles.copyrightContainer}>
              {renderCopyright()}
              <div className={styles.digitalSpidersLink}>
                <p>
                  Built by
                  <TextLink
                    className={clsx(styles.textLink, styles.override)}
                    to={`https://www.digitalspiders.io/?utm_source=${SITE_DOMAIN}`}
                    withExternalIcon={false}
                  >
                    Digital Spiders
                  </TextLink>
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className={clsx(styles.footerContainer, styles.simpleFooter)}>{renderCopyright()}</div>
      )}
    </footer>
  );
};

export default Footer;
